import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Upload, Image as ImageIcon, Trash2, FolderPlus, ArrowLeft, X, Loader2, Star, Settings, Download, CheckSquare, Square, HardDrive } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
// Iter 167 — reverted from PhotoSwipe v5 back to `yet-another-react-lightbox`
// per user preference. Still uses the WebP preview endpoint + the 400px
// thumbnail as thumbnail, so the fast-open behaviour we built in Iter 157 is
// preserved. See PhotoLightbox below.
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Counter from "yet-another-react-lightbox/plugins/counter";
import Captions from "yet-another-react-lightbox/plugins/captions";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/counter.css";
import "yet-another-react-lightbox/plugins/captions.css";

const NAVY = "#0A2463";
const RED = "#C8102E";

// Iter 159 — module-level helper because AlbumCard, downloadAlbum, and
// downloadSelected all trigger the same responseType:"blob" download and
// need to unwrap JSON errors delivered as Blobs.
async function readBlobErrorDetail(err) {
    try {
        const data = err.response?.data;
        if (!data) return null;
        if (typeof data === "string") return data;
        if (data instanceof Blob) {
            const text = await data.text();
            try { return JSON.parse(text).detail; } catch { return text.slice(0, 200); }
        }
        return data.detail || null;
    } catch { return null; }
}

function triggerDownload(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
}

const CATEGORY_LABELS = {
    anniversary: "Anniversaries",
    ceremony: "Ceremonies",
    conference: "Conferences",
    tournament: "Tournaments",
    line: "Lines",
    community: "Community Service",
    other: "Other",
};

export default function Photos() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { albumId } = useParams();
    const [albums, setAlbums] = useState([]);
    const [activeAlbum, setActiveAlbum] = useState(null);
    const [photos, setPhotos] = useState([]);
    const [photosTotal, setPhotosTotal] = useState(0);
    const [loadingMore, setLoadingMore] = useState(false);
    const [creatingAlbum, setCreatingAlbum] = useState(false);
    const [editingAlbum, setEditingAlbum] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [loadingPhotos, setLoadingPhotos] = useState(false);
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [selectMode, setSelectMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [downloading, setDownloading] = useState(false);
    // Lightbox state — index into the currently-visible `photos` array. `null`
    // means closed. Kept at parent level so prev/next can walk the whole album.
    const [lightboxIndex, setLightboxIndex] = useState(null);

    // Page size for infinite scroll. 60 tiles = ~5MB of thumbnails at
    // 400px JPEG q=82. That's enough for the first paint on any album,
    // even one with 200+ photos.
    const PAGE_SIZE = 60;

    function openAlbum(album) {
        // Drive the active album from the URL so a hard refresh keeps the
        // user inside the album they were viewing.
        navigate(`/photos/${album.id}`);
    }
    function closeAlbum() {
        navigate("/photos");
    }

    async function loadAlbums() {
        try {
            const { data } = await api.get("/photos/albums");
            setAlbums(data || []);
            return data || [];
        } catch { setAlbums([]); return []; }
    }
    async function loadPhotos(albumName) {
        setLoadingPhotos(true);
        try {
            // Fetch the first page + total count in parallel so the grid
            // can render immediately and know when to stop scrolling.
            const [page, count] = await Promise.all([
                api.get(`/photos?album=${encodeURIComponent(albumName)}&limit=${PAGE_SIZE}&offset=0`),
                api.get(`/photos/count?album=${encodeURIComponent(albumName)}`),
            ]);
            setPhotos(page.data || []);
            setPhotosTotal(count.data?.total || (page.data || []).length);
        } catch {
            setPhotos([]);
            setPhotosTotal(0);
        }
        setLoadingPhotos(false);
    }

    async function loadMorePhotos() {
        if (!activeAlbum || loadingMore) return;
        if (photos.length >= photosTotal) return;
        setLoadingMore(true);
        try {
            const { data } = await api.get(
                `/photos?album=${encodeURIComponent(activeAlbum.name)}&limit=${PAGE_SIZE}&offset=${photos.length}`,
            );
            // De-dupe by id in case a photo was uploaded while paging (shift
            // would otherwise cause a repeat at page boundaries).
            const existing = new Set(photos.map((p) => p.id));
            const fresh = (data || []).filter((p) => !existing.has(p.id));
            setPhotos((prev) => [...prev, ...fresh]);
        } catch { /* ignore — user can retry by scrolling again */ }
        setLoadingMore(false);
    }

    // Initial album load
    useEffect(() => { loadAlbums(); }, []);

    // Reconcile the URL `:albumId` with the loaded album list. If the URL
    // points at a real album, open it. If the album no longer exists, redirect
    // back to the grid (avoids a blank "loading forever" state on a stale id).
    useEffect(() => {
        if (!albumId) {
            setActiveAlbum(null);
            return;
        }
        if (albums.length === 0) return; // wait for albums to load
        const match = albums.find((a) => a.id === albumId);
        if (match) {
            setActiveAlbum(match);
        } else {
            navigate("/photos", { replace: true });
        }
    }, [albumId, albums, navigate]);

    useEffect(() => {
        if (activeAlbum) loadPhotos(activeAlbum.name);
        else setPhotos([]);
        setSelectMode(false);
        setSelectedIds([]);
    }, [activeAlbum]);

    function toggleSelect(id) {
        setSelectedIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
    }

    // Iter 159 — full-blob download error handling. See top-level
    // `readBlobErrorDetail` for the axios-blob → JSON-detail unwrap logic.
    async function downloadAlbum() {
        if (!activeAlbum) return;
        setDownloading(true);
        try {
            const res = await api.post("/photos/download-zip", { album: activeAlbum.name }, { responseType: "blob" });
            triggerDownload(res.data, `aop-${activeAlbum.name.replace(/[^a-z0-9 -]/gi, "_")}.zip`);
            toast.success(`Downloaded "${activeAlbum.name}"`);
        } catch (e) {
            const detail = await readBlobErrorDetail(e);
            toast.error(detail || "Download failed. Try selecting fewer photos or contact an admin.");
        }
        setDownloading(false);
    }

    async function downloadSelected() {
        if (selectedIds.length === 0) return;
        setDownloading(true);
        try {
            const res = await api.post("/photos/download-zip", { photo_ids: selectedIds }, { responseType: "blob" });
            triggerDownload(res.data, `aop-photos-${selectedIds.length}.zip`);
            toast.success(`Downloaded ${selectedIds.length} photo${selectedIds.length === 1 ? "" : "s"}`);
            setSelectMode(false); setSelectedIds([]);
        } catch (e) {
            const detail = await readBlobErrorDetail(e);
            toast.error(detail || "Download failed. Try selecting fewer photos or contact an admin.");
        }
        setDownloading(false);
    }

    async function removePhoto(id) {
        if (!window.confirm("Delete this photo?")) return;
        try {
            await api.delete(`/photos/${id}`);
            // Iter 169 — optimistic UI + no full-list refetch. Splice
            // the tile out locally and decrement the total. Previously
            // we called `loadPhotos(activeAlbum.name)` + `loadAlbums()`
            // here which triggered a fresh 60-photo query plus 60
            // parallel thumb GETs on the pod — piling memory pressure
            // right when the backend was still doing the R2 batch
            // delete in a background task. That combined spike was
            // OOM-killing the 512 Mi pod on production deletes.
            setPhotos((prev) => prev.filter((p) => p.id !== id));
            setPhotosTotal((prev) => Math.max(0, prev - 1));
            toast.success("Photo deleted");
        } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
    }

    async function setAsCover(photoId) {
        try {
            await api.put(`/photos/albums/${activeAlbum.id}`, { cover_photo_id: photoId });
            toast.success("Cover photo updated");
            await loadAlbums();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed to set cover"); }
    }

    async function removeAlbum(album) {
        const count = album.count || 0;
        const photoLine = count > 0
            ? ` All ${count} photo${count === 1 ? "" : "s"} inside will also be permanently removed.`
            : "";
        const warn = album.is_default
            ? `Delete the default "${album.name}" album?${photoLine} It will not be re-created automatically.`
            : `Delete the "${album.name}" album?${photoLine}`;
        if (!window.confirm(warn)) return;
        try {
            const { data } = await api.delete(`/photos/albums/${album.id}`);
            const removed = data?.photos_deleted || 0;
            toast.success(removed > 0
                ? `Album removed · ${removed} photo${removed === 1 ? "" : "s"} deleted`
                : "Album removed");
            await loadAlbums();
            if (activeAlbum?.id === album.id) closeAlbum();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    }

    const visibleAlbums = categoryFilter === "all" ? albums : albums.filter((a) => a.category === categoryFilter);
    const counts = albums.reduce((acc, a) => { acc[a.category || "other"] = (acc[a.category || "other"] || 0) + 1; return acc; }, {});

    return (
        <div className="bg-slate-50 min-h-screen">
            <section className="border-b-2 bg-white" style={{ borderColor: NAVY }}>
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
                    <div className="flex flex-wrap items-end justify-between gap-4">
                        <div>
                            {activeAlbum && (
                                <button onClick={closeAlbum} className="text-sm text-slate-500 hover:text-primary inline-flex items-center gap-1 mb-2" data-testid="back-to-albums">
                                    <ArrowLeft className="h-4 w-4" /> All albums
                                </button>
                            )}
                            <div className="text-xs uppercase tracking-[0.3em] font-bold mb-1" style={{ color: RED }}>Photo Gallery</div>
                            <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter" style={{ color: NAVY }}>
                                {activeAlbum ? activeAlbum.name : "Albums"}
                            </h1>
                            <p className="text-slate-600 mt-2">
                                {activeAlbum
                                    ? `${photos.length} photo${photos.length === 1 ? "" : "s"} in this album. Click the ⭐ on any photo to make it the cover.`
                                    : `${albums.length} album${albums.length === 1 ? "" : "s"} · ${albums.reduce((a, b) => a + (b.count || 0), 0)} photos total.`}
                            </p>
                        </div>
                        {user && (
                            <div className="flex flex-wrap gap-2">
                                {!activeAlbum && (
                                    <Button onClick={() => setCreatingAlbum(true)} variant="outline" className="rounded-full" data-testid="create-album-btn">
                                        <FolderPlus className="h-4 w-4 mr-1.5" /> New album
                                    </Button>
                                )}
                                {activeAlbum && (
                                    <>
                                        {!selectMode ? (
                                            <>
                                                <Button onClick={() => setSelectMode(true)} variant="outline" className="rounded-full" data-testid="select-photos-btn">
                                                    <CheckSquare className="h-4 w-4 mr-1.5" /> Select
                                                </Button>
                                                <Button onClick={downloadAlbum} disabled={downloading || photos.length === 0} variant="outline" className="rounded-full" data-testid="download-album-btn">
                                                    {downloading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}
                                                    Download album
                                                </Button>
                                            </>
                                        ) : (
                                            <>
                                                <Button onClick={() => { setSelectMode(false); setSelectedIds([]); }} variant="outline" className="rounded-full" data-testid="select-cancel-btn">
                                                    <X className="h-4 w-4 mr-1.5" /> Cancel
                                                </Button>
                                                <Button onClick={downloadSelected} disabled={downloading || selectedIds.length === 0} className="rounded-full bg-primary hover:bg-primary/90 text-white" data-testid="download-selected-btn">
                                                    {downloading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}
                                                    Download {selectedIds.length || ""}
                                                </Button>
                                            </>
                                        )}
                                        <UploadButton album={activeAlbum.name} onDone={async () => { await loadPhotos(activeAlbum.name); await loadAlbums(); }} disabled={uploading} setUploading={setUploading} />
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                    {!activeAlbum && (
                        <CategoryFilter value={categoryFilter} onChange={setCategoryFilter} counts={counts} total={albums.length} />
                    )}
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
                {!activeAlbum ? (
                    <>
                        {user?.role === "admin" && <StorageQuotaWidget />}
                        <AlbumGrid albums={visibleAlbums} onOpen={openAlbum} onDelete={removeAlbum} onEdit={setEditingAlbum} currentUser={user} />
                    </>
                ) : loadingPhotos ? (
                    <div className="grid place-items-center py-24"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>
                ) : photos.length === 0 ? (
                    <div className="bg-white border-2 border-dashed border-slate-300 rounded-3xl p-16 text-center">
                        <ImageIcon className="h-12 w-12 mx-auto text-slate-400" />
                        <p className="mt-4 font-heading text-xl" style={{ color: NAVY }}>No photos yet</p>
                        <p className="text-sm text-slate-500">Click "Upload photos" above to start the memories.</p>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="photo-grid">
                            {photos.map((p, i) => (
                                <PhotoTile
                                    key={p.id}
                                    photo={p}
                                    currentUser={user}
                                    onDelete={removePhoto}
                                    onSetCover={(albumCanEdit) => albumCanEdit ? setAsCover(p.id) : null}
                                    albumCanEdit={user && (user.role === "admin" || activeAlbum?.created_by === user.id)}
                                    selectMode={selectMode}
                                    selected={selectedIds.includes(p.id)}
                                    onToggleSelect={() => toggleSelect(p.id)}
                                    onOpenLightbox={() => setLightboxIndex(i)}
                                />
                            ))}
                        </div>
                        <InfiniteScrollSentinel
                            hasMore={photos.length < photosTotal}
                            loading={loadingMore}
                            onLoadMore={loadMorePhotos}
                            shown={photos.length}
                            total={photosTotal}
                        />
                    </>
                )}
            </section>

            <CreateAlbumDialog open={creatingAlbum} onClose={() => setCreatingAlbum(false)} onCreated={async (a) => { setCreatingAlbum(false); await loadAlbums(); navigate(`/photos/${a.id}`); }} />
            <EditAlbumDialog album={editingAlbum} onClose={() => setEditingAlbum(null)} onSaved={async () => { setEditingAlbum(null); await loadAlbums(); }} />
            <PhotoLightbox
                photos={photos}
                index={lightboxIndex}
                onClose={() => setLightboxIndex(null)}
            />
        </div>
    );
}

/**
 * Bottom-of-grid sentinel that triggers `onLoadMore` via IntersectionObserver
 * whenever the grid reaches 400px from the bottom of the viewport. That
 * pre-loads the next chunk of photos BEFORE the user actually reaches the
 * bottom, so scrolling feels seamless on a 190-photo album.
 *
 * Also renders a "Showing X of Y" hint + spinner while more photos are
 * being fetched. Hides itself completely when all photos are loaded.
 */
function InfiniteScrollSentinel({ hasMore, loading, onLoadMore, shown, total }) {
    const ref = useRef(null);
    useEffect(() => {
        if (!hasMore || !ref.current) return;
        const el = ref.current;
        const io = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting)) onLoadMore();
            },
            { rootMargin: "400px" },
        );
        io.observe(el);
        return () => io.disconnect();
    }, [hasMore, onLoadMore, shown]);

    if (!hasMore && total === 0) return null;
    return (
        <div
            ref={ref}
            className="mt-6 flex flex-col items-center gap-2 text-sm text-slate-500"
            data-testid="photos-infinite-sentinel"
        >
            {hasMore && (
                loading ? (
                    <div className="flex items-center gap-2" data-testid="photos-loading-more">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Loading more photos…</span>
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={onLoadMore}
                        className="text-primary hover:underline"
                        data-testid="photos-load-more-btn"
                    >
                        Load more
                    </button>
                )
            )}
            {total > 0 && (
                <div className="text-xs text-slate-400" data-testid="photos-count-indicator">
                    Showing {shown} of {total}
                </div>
            )}
        </div>
    );
}


function CategoryFilter({ value, onChange, counts, total }) {
    const all = ["all", ...Object.keys(CATEGORY_LABELS)];
    return (
        <div className="flex flex-wrap gap-2 mt-6" data-testid="category-filter">
            {all.map((c) => {
                const n = c === "all" ? total : (counts[c] || 0);
                if (c !== "all" && n === 0) return null;
                const active = value === c;
                return (
                    <button
                        key={c}
                        onClick={() => onChange(c)}
                        className={`text-xs px-3 py-1.5 rounded-full font-semibold uppercase tracking-wide transition-all ${active ? "bg-primary text-white shadow-warm" : "bg-white text-slate-600 border border-slate-200 hover:border-primary"}`}
                        data-testid={`category-pill-${c}`}
                    >
                        {c === "all" ? `All (${n})` : `${CATEGORY_LABELS[c]} (${n})`}
                    </button>
                );
            })}
        </div>
    );
}

function AlbumGrid({ albums, onOpen, onDelete, onEdit, currentUser }) {
    if (albums.length === 0) {
        return (
            <div className="bg-white border-2 border-dashed border-slate-300 rounded-3xl p-16 text-center">
                <ImageIcon className="h-12 w-12 mx-auto text-slate-400" />
                <p className="mt-4 font-heading text-xl" style={{ color: NAVY }}>No albums in this category</p>
                <p className="text-sm text-slate-500">Switch categories or create a new album.</p>
            </div>
        );
    }
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="album-grid">
            {albums.map((a) => (
                <AlbumCard key={a.id || a.name} album={a} onOpen={() => onOpen(a)} onDelete={() => onDelete(a)} onEdit={() => onEdit(a)} currentUser={currentUser} />
            ))}
        </div>
    );
}

// Iter 161 — Admin-only storage quota widget on the Photos landing page.
// Shows total MB and a top-N breakdown of the biggest albums so you can
// spot bloat at a glance. Sorted server-side by bytes desc.
function StorageQuotaWidget() {
    const [stats, setStats] = useState(null);
    const [err, setErr] = useState("");
    const [expanded, setExpanded] = useState(false);
    // Iter 168 — reclaim (R2 purge) state.
    const [reclaiming, setReclaiming] = useState(false);
    const [reclaimed, setReclaimed] = useState(null);

    async function loadStats() {
        try {
            const { data } = await api.get("/photos/albums/storage-stats");
            setStats(data);
            setErr("");
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        }
    }

    useEffect(() => { loadStats(); }, []);

    async function reclaim() {
        setReclaiming(true);
        setReclaimed(null);
        let photos = 0;
        let keys = 0;
        try {
            for (let i = 0; i < 100; i++) {
                const { data } = await api.post("/photos/purge-deleted?limit=50");
                photos += data.purged_photos || 0;
                keys += data.purged_keys || 0;
                if (!data.has_more) break;
            }
            setReclaimed({ photos, keys });
            toast.success(
                photos > 0
                    ? `Reclaimed ${keys} R2 object${keys === 1 ? "" : "s"} from ${photos} deleted photo${photos === 1 ? "" : "s"}`
                    : "Nothing to reclaim — every deleted photo is already purged from R2"
            );
            await loadStats();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Reclaim failed");
        } finally {
            setReclaiming(false);
        }
    }

    if (err) {
        return (
            <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm" data-testid="storage-quota-error">
                Storage stats: {err}
            </div>
        );
    }
    if (!stats) {
        return (
            <div className="mb-6 h-24 rounded-2xl bg-slate-100 animate-pulse" data-testid="storage-quota-loading" />
        );
    }

    const albums = stats.albums || [];
    const shown = expanded ? albums : albums.slice(0, 5);
    const maxMb = albums.length > 0 ? Math.max(...albums.map((a) => a.mb || 0), 0.01) : 0.01;
    const downscalePct = stats.total_photos > 0
        ? Math.round((stats.total_downscaled / stats.total_photos) * 100)
        : 0;

    return (
        <div className="mb-8 bg-white border border-slate-200 rounded-2xl p-5 shadow-sm" data-testid="storage-quota-widget">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full grid place-items-center" style={{ backgroundColor: `${NAVY}15`, color: NAVY }}>
                        <HardDrive className="h-5 w-5" />
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Storage used</div>
                        <div className="font-heading text-2xl font-black leading-tight" style={{ color: NAVY }}>
                            {stats.total_mb.toFixed(1)} MB
                            <span className="text-sm text-slate-500 font-normal ml-2">across {stats.total_photos} photo{stats.total_photos === 1 ? "" : "s"}</span>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-6 text-sm">
                    <div>
                        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Downscaled</div>
                        <div className="font-semibold" style={{ color: NAVY }}>
                            {stats.total_downscaled} / {stats.total_photos}
                            <span className="text-slate-500 font-normal ml-1">({downscalePct}%)</span>
                        </div>
                    </div>
                    {stats.saved_mb > 0 && (
                        <div>
                            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Saved</div>
                            <div className="font-semibold text-emerald-700">{stats.saved_mb.toFixed(1)} MB</div>
                        </div>
                    )}
                </div>
            </div>

            {/* Iter 168 — reclaim R2 bytes for photos already soft-deleted. */}
            {(stats.pending_purge_count > 0 || reclaimed) && (
                <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-center justify-between gap-3" data-testid="storage-reclaim-cta">
                    <div className="text-sm text-amber-900">
                        {reclaimed
                            ? <>Reclaimed <strong>{reclaimed.keys}</strong> R2 objects from <strong>{reclaimed.photos}</strong> deleted photos.</>
                            : <><strong>{stats.pending_purge_count}</strong> deleted {stats.pending_purge_count === 1 ? "photo is" : "photos are"} still holding R2 bytes. Reclaim them to free up your 10 GB.</>
                        }
                    </div>
                    <button
                        type="button"
                        onClick={reclaim}
                        disabled={reclaiming}
                        data-testid="storage-reclaim-btn"
                        className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white text-xs font-semibold px-3 py-1.5 transition"
                    >
                        {reclaiming ? "Reclaiming…" : reclaimed ? "Run again" : "Reclaim R2"}
                    </button>
                </div>
            )}

            {albums.length === 0 ? (
                <p className="mt-4 text-sm text-slate-500">No photos uploaded yet.</p>
            ) : (
                <div className="mt-4 space-y-2" data-testid="storage-quota-albums">
                    {shown.map((a) => {
                        const pct = (a.mb / maxMb) * 100;
                        // Only flag "legacy" when there are un-downscaled photos AND the
                        // album is heavy enough that the backfill's 500 KB min_bytes
                        // filter would actually touch it. Otherwise the amber tag
                        // shouts at tiny 30 KB albums the admin can safely ignore.
                        const legacyLeft = a.photo_count - a.downscaled_count;
                        const flagLegacy = legacyLeft > 0 && a.bytes >= 500 * 1024;
                        // Iter 162 — orphan bucket = photos whose album was deleted
                        // but whose bytes still live in storage. Render dimmed +
                        // slate so it's obviously not a live album.
                        const isOrphan = !!a.orphaned;
                        return (
                            <div key={a.album} className="flex items-center gap-3 text-sm" data-testid={isOrphan ? "storage-quota-orphan-row" : undefined}>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-baseline justify-between gap-2">
                                        <span className={`font-medium truncate ${isOrphan ? "italic text-slate-500" : ""}`} style={isOrphan ? undefined : { color: NAVY }}>
                                            {a.album}
                                        </span>
                                        <span className="text-slate-600 tabular-nums shrink-0">
                                            {a.mb.toFixed(2)} MB
                                            <span className="text-slate-400 ml-2">· {a.photo_count} photo{a.photo_count === 1 ? "" : "s"}</span>
                                            {flagLegacy && (
                                                <span className="ml-2 text-amber-700">· {legacyLeft} legacy</span>
                                            )}
                                        </span>
                                    </div>
                                    <div className="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                                        <div
                                            className="h-full rounded-full"
                                            style={{ width: `${Math.max(pct, 2)}%`, backgroundColor: isOrphan ? "#94A3B8" : (flagLegacy ? "#F59E0B" : RED) }}
                                        />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    {albums.length > 5 && (
                        <button
                            type="button"
                            onClick={() => setExpanded((v) => !v)}
                            className="text-xs font-semibold text-slate-500 hover:text-slate-800 mt-1"
                            data-testid="storage-quota-toggle"
                        >
                            {expanded ? "Show top 5 only" : `Show all ${albums.length} albums`}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

function AlbumCard({ album, onOpen, onDelete, onEdit, currentUser }) {
    const canEdit = currentUser && (currentUser.role === "admin" || album.created_by === currentUser.id);
    // Admins can delete any album (including default canonical ones). Members
    // can only delete their own custom albums.
    const canDelete = currentUser && (
        currentUser.role === "admin" ||
        (album.created_by === currentUser.id && !album.is_default)
    );
    return (
        <div
            onClick={onOpen}
            className="group cursor-pointer relative bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-warm hover:-translate-y-1 hover:shadow-warm-lg transition-all"
            data-testid={`album-card-${album.name}`}
        >
            <div className="aspect-[4/3] bg-gradient-to-br from-primary/15 to-primary/5 grid place-items-center relative overflow-hidden">
                {album.cover_url ? (() => {
                    // Iter 158 — responsive srcset for album covers too.
                    // Cover is a 4:3 tile at 1 / 2 / 3 columns depending on
                    // breakpoint, so we need 320-800px variants.
                    const coverPath = (album.cover_thumb_url || album.cover_url || "").split("?")[0];
                    const looksThumb = coverPath.includes("/photos/thumb/");
                    const src = looksThumb ? mediaUrl(`${coverPath}?w=600`) : mediaUrl(album.cover_thumb_url || album.cover_url);
                    const srcSet = looksThumb ? [320, 600, 800].map((w) => `${mediaUrl(`${coverPath}?w=${w}`)} ${w}w`).join(", ") : undefined;
                    const sizes = looksThumb ? "(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" : undefined;
                    return (
                        <img
                            src={src}
                            srcSet={srcSet}
                            sizes={sizes}
                            alt={album.name}
                            loading="lazy"
                            decoding="async"
                            fetchPriority="low"
                            className="absolute inset-0 w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                            data-testid={`album-cover-${album.name}`}
                        />
                    );
                })() : (
                    <ImageIcon className="h-12 w-12 text-primary/40" />
                )}
                <span className="absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wider bg-white/95 px-2 py-1 rounded-full backdrop-blur" style={{ color: "#334155" }}>
                    {CATEGORY_LABELS[album.category] || "Other"}
                </span>
            </div>
            <div className="p-4">
                <h3 className="font-heading font-black text-lg leading-tight truncate" style={{ color: NAVY }}>{album.name}</h3>
                <div className="text-xs text-slate-500 mt-1">
                    {album.count || 0} photo{album.count === 1 ? "" : "s"}
                    {album.is_default ? " · Official album" : album.created_by_name ? ` · by ${album.created_by_name}` : ""}
                </div>
            </div>
            <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                    onClick={async (e) => {
                        e.stopPropagation();
                        try {
                            const res = await api.post("/photos/download-zip", { album: album.name }, { responseType: "blob" });
                            triggerDownload(res.data, `aop-${album.name.replace(/[^a-z0-9 -]/gi, "_")}.zip`);
                            toast.success(`Downloaded "${album.name}"`);
                        } catch (err) {
                            const detail = await readBlobErrorDetail(err);
                            toast.error(detail || "Download failed. Try a smaller selection.");
                        }
                    }}
                    className="rounded-full bg-white/95 hover:bg-white p-1.5 shadow text-primary"
                    title="Download album"
                    data-testid={`download-album-card-${album.name}`}
                >
                    <Download className="h-3.5 w-3.5" />
                </button>
                {canEdit && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onEdit(); }}
                        className="rounded-full bg-white/95 hover:bg-white p-1.5 shadow text-slate-700"
                        data-testid={`edit-album-${album.name}`}
                        title="Edit album"
                    >
                        <Settings className="h-3.5 w-3.5" />
                    </button>
                )}
                {canDelete && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onDelete(); }}
                        className="rounded-full bg-white/95 hover:bg-white p-1.5 shadow text-destructive"
                        data-testid={`delete-album-${album.name}`}
                    >
                        <Trash2 className="h-3.5 w-3.5" />
                    </button>
                )}
            </div>
        </div>
    );
}

function PhotoTile({ photo, currentUser, onDelete, onSetCover, albumCanEdit, selectMode, selected, onToggleSelect, onOpenLightbox }) {
    // Native <img src> — the browser handles fetch, cache, HTTP/2 multiplexing,
    // viewport-based lazy load, and progressive decode natively. The previous
    // `api.get(..., {responseType: 'blob'})` pattern serialised 50 XHR
    // downloads of the ORIGINAL (2-8MB per phone photo) through the JS thread
    // — that's what made 50-photo albums crawl. `thumb_url` is a JPEG
    // generated once by the backend and cached.
    //
    // Iter 158 — responsive srcset. The backend accepts w=200/320/400/600/800
    // so we hand the browser three variants and let it pick the best one
    // based on the tile's rendered pixel size (device DPR × CSS px).
    // `sizes` mirrors the grid: 2-col mobile → tile ~50vw, 3-col sm → 33vw,
    // 4-col lg → 25vw. `fetchPriority=low` de-prioritises off-screen tiles
    // when the browser prefetches them, keeping the initial paint snappy.
    const thumbPath = (photo.thumb_url || "").split("?")[0] || photo.url;
    const thumbSrc = mediaUrl(`${thumbPath}?w=400`);
    const thumbSrcSet = [200, 400, 800]
        .map((w) => `${mediaUrl(`${thumbPath}?w=${w}`)} ${w}w`)
        .join(", ");
    const thumbSizes = "(min-width: 1024px) 25vw, (min-width: 640px) 33vw, 50vw";

    const canDelete = currentUser && (currentUser.role === "admin" || currentUser.id === photo.uploaded_by);

    async function downloadOne(e) {
        e.stopPropagation();
        try {
            const path = photo.url.startsWith("/api") ? photo.url.slice(4) : photo.url;
            const res = await api.get(path, { responseType: "blob" });
            const ext = (photo.original_filename || photo.url).split(".").pop().toLowerCase();
            const base = (photo.title || photo.original_filename || "photo").replace(/[^a-z0-9._ -]/gi, "_");
            const name = base.toLowerCase().endsWith(`.${ext}`) ? base : `${base}.${ext}`;
            triggerDownload(res.data, name);
        } catch (e) { toast.error("Download failed"); }
    }

    if (selectMode) {
        return (
            <button
                type="button"
                onClick={onToggleSelect}
                className={`group relative aspect-square rounded-2xl overflow-hidden bg-muted border-4 shadow-warm transition-all ${selected ? "border-primary" : "border-transparent hover:border-primary/40"}`}
                data-testid={`photo-select-${photo.id}`}
            >
                <img
                    src={thumbSrc}
                    srcSet={thumbSrcSet}
                    sizes={thumbSizes}
                    alt={photo.title || "Photo"}
                    loading="lazy"
                    decoding="async"
                    fetchPriority="low"
                    className="w-full h-full object-contain"
                />
                <div className={`absolute top-2 left-2 rounded-full p-1.5 shadow ${selected ? "bg-primary text-white" : "bg-white/90 text-slate-400"}`}>
                    {selected ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                </div>
            </button>
        );
    }

    return (
        <div className="group relative aspect-square rounded-2xl overflow-hidden bg-muted border border-slate-200 shadow-warm" data-testid={`photo-${photo.id}`}>
            <button
                type="button"
                onClick={onOpenLightbox}
                className="absolute inset-0 w-full h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary cursor-zoom-in"
                aria-label={`Open ${photo.title || "photo"}`}
                data-testid={`photo-open-${photo.id}`}
            >
                {thumbSrc ? (
                    <img
                        src={thumbSrc}
                        srcSet={thumbSrcSet}
                        sizes={thumbSizes}
                        alt={photo.title || "Photo"}
                        loading="lazy"
                        decoding="async"
                        fetchPriority="low"
                        className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                    />
                ) : (
                    <div className="w-full h-full animate-pulse bg-muted" />
                )}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3 text-white opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    <div className="text-xs font-semibold truncate text-left">{photo.title || "Untitled"}</div>
                    <div className="text-[10px] opacity-80 text-left">
                        by {photo.uploaded_by_name} · {photo.created_at && format(parseISO(photo.created_at), "MMM d")}
                    </div>
                </div>
            </button>
            <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                <button
                    onClick={downloadOne}
                    className="rounded-full bg-white/95 hover:bg-white p-1.5 shadow text-primary"
                    title="Download photo"
                    data-testid={`download-photo-${photo.id}`}
                >
                    <Download className="h-3.5 w-3.5" />
                </button>
                {albumCanEdit && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onSetCover(true); }}
                        className="rounded-full bg-white/95 hover:bg-white p-1.5 shadow text-amber-500"
                        title="Make album cover"
                        data-testid={`set-cover-${photo.id}`}
                    >
                        <Star className="h-3.5 w-3.5" />
                    </button>
                )}
                {canDelete && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onDelete(photo.id); }}
                        className="rounded-full bg-white/90 hover:bg-white p-1.5 shadow"
                        data-testid={`delete-photo-${photo.id}`}
                    >
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </button>
                )}
            </div>
        </div>
    );
}

/**
 * Iter 167 — photo viewer backed by `yet-another-react-lightbox`.
 *
 * Keeps the fast-open trick from Iter 157: the WebP preview endpoint
 * (`/api/photos/preview/{path}`) serves the `src`, while the 400 px thumb
 * URL (already cached by the grid tile) is passed as `thumbnail` so the
 * user sees SOMETHING the moment they click. Full-res originals are only
 * fetched when the user explicitly hits Download.
 *
 * Parent-owned state contract:
 *   • `index === null` → closed
 *   • valid integer → open at that slide
 */
function PhotoLightbox({ photos, index, onClose }) {
    async function downloadCurrent(idx) {
        const p = photos[idx];
        if (!p) return;
        try {
            const path = p.url.startsWith("/api") ? p.url.slice(4) : p.url;
            const res = await api.get(path, { responseType: "blob" });
            const ext = (p.original_filename || p.url).split(".").pop().toLowerCase();
            const base = (p.title || p.original_filename || "photo").replace(/[^a-z0-9._ -]/gi, "_");
            const name = base.toLowerCase().endsWith(`.${ext}`) ? base : `${base}.${ext}`;
            triggerDownload(res.data, name);
        } catch {
            toast.error("Download failed");
        }
    }

    const slides = photos.map((p) => {
        const dateLine = p.created_at ? ` · ${format(parseISO(p.created_at), "MMM d, yyyy")}` : "";
        return {
            src: mediaUrl(p.preview_url || p.url),
            // Thumb pass-through — lets the Captions plugin also render one.
            thumbnail: mediaUrl(p.thumb_url),
            alt: p.title || "Photo",
            title: p.title || "Untitled",
            description: `by ${p.uploaded_by_name || "Unknown"}${dateLine}`,
        };
    });

    return (
        <Lightbox
            open={index !== null && index >= 0 && index < photos.length}
            close={onClose}
            slides={slides}
            index={index ?? 0}
            plugins={[Zoom, Counter, Captions, Fullscreen]}
            counter={{ container: { style: { top: "unset", bottom: 8, left: 8 } } }}
            captions={{ descriptionTextAlign: "center", descriptionMaxLines: 2 }}
            zoom={{ maxZoomPixelRatio: 3, scrollToZoom: true }}
            carousel={{ finite: false, preload: 2 }}
            controller={{ closeOnBackdropClick: true }}
            styles={{ container: { backgroundColor: "rgba(0,0,0,0.94)" } }}
            toolbar={{
                buttons: [
                    <button
                        key="download"
                        type="button"
                        className="yarl__button"
                        aria-label="Download original"
                        title="Download original"
                        onClick={() => {
                            const el = document.querySelector(".yarl__slide_current");
                            const idx = el?.dataset?.slideIndex ?? index;
                            downloadCurrent(Number(idx));
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <path d="M7 10l5 5 5-5" />
                            <path d="M12 15V3" />
                        </svg>
                    </button>,
                    "close",
                ],
            }}
        />
    );
}

function UploadButton({ album, onDone, disabled, setUploading }) {
    const inputRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState({ done: 0, total: 0 });

    async function pick(files) {
        if (!files || files.length === 0) return;
        // Upload photos one at a time using the single-photo endpoint. This
        // avoids the Cloudflare 100MB payload limit that was causing the bulk
        // endpoint to 413/502, which in turn appeared to the user as a forced
        // logout + "Cloudflare parse error" on the very next request.
        //
        // Each upload is independent: a single failure does NOT cancel the
        // rest of the batch or touch auth state. We tally successes / failures
        // and surface a single summary toast at the end.
        setBusy(true); setUploading(true);
        setProgress({ done: 0, total: files.length });
        let ok = 0;
        const failures = [];
        for (let i = 0; i < files.length; i++) {
            const f = files[i];
            try {
                if (f.size > 10 * 1024 * 1024) {
                    failures.push({ name: f.name, error: "Over 10MB" });
                    continue;
                }
                const fd = new FormData();
                fd.append("file", f);
                fd.append("title", "");
                fd.append("album", album);
                await api.post("/photos", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                    // 60s per file is plenty for a 10MB image even on a slow
                    // connection; without it axios uses no timeout at all.
                    timeout: 60000,
                });
                ok += 1;
            } catch (e) {
                const detail = e.response?.data?.detail || e.message || "Upload failed";
                failures.push({ name: f.name, error: detail });
            } finally {
                setProgress({ done: i + 1, total: files.length });
            }
        }
        if (ok && failures.length === 0) toast.success(`Uploaded ${ok} photo${ok === 1 ? "" : "s"}`);
        else if (ok && failures.length) toast.warning(`Uploaded ${ok}, but ${failures.length} failed`);
        else toast.error(`All ${failures.length} uploads failed`);
        // Await the parent's refresh so the new photos appear before we
        // drop the "Uploading…" label.
        try { await onDone?.(); } catch { /* ignore */ }
        setBusy(false); setUploading(false);
        setProgress({ done: 0, total: 0 });
        if (inputRef.current) inputRef.current.value = "";
    }

    const showProgress = busy && progress.total > 0;
    return (
        <>
            <input
                ref={inputRef}
                type="file"
                multiple
                accept="image/*"
                className="hidden"
                onChange={(e) => pick(Array.from(e.target.files || []))}
                data-testid="photos-multi-input"
            />
            <Button
                onClick={() => inputRef.current?.click()}
                disabled={busy || disabled}
                className="rounded-full bg-primary hover:bg-primary/90 shadow-warm text-white"
                data-testid="upload-photos-btn"
            >
                {busy ? (
                    <>
                        <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                        {showProgress ? `Uploading ${progress.done}/${progress.total}…` : "Uploading…"}
                    </>
                ) : (
                    <><Upload className="h-4 w-4 mr-1.5" /> Upload photos</>
                )}
            </Button>
        </>
    );
}

function CreateAlbumDialog({ open, onClose, onCreated }) {
    const [name, setName] = useState("");
    const [category, setCategory] = useState("other");
    const [busy, setBusy] = useState(false);

    useEffect(() => { if (open) { setName(""); setCategory("other"); } }, [open]);

    async function create() {
        const value = name.trim();
        if (!value) { toast.error("Give the album a name"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/photos/albums", { name: value, category });
            toast.success(`Album "${data.name}" created`);
            onCreated?.(data);
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        setBusy(false);
    }

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl" style={{ color: NAVY }}>New album</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 mt-2">
                    <div>
                        <Label className="text-slate-900">Album name</Label>
                        <Input
                            value={name}
                            autoFocus
                            onChange={(e) => setName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter" && !busy) create(); }}
                            placeholder="e.g. Commitment Ceremony 2031"
                            className="rounded-xl mt-1.5"
                            data-testid="album-name-input"
                        />
                    </div>
                    <div>
                        <Label className="text-slate-900">Category</Label>
                        <Select value={category} onValueChange={setCategory}>
                            <SelectTrigger className="rounded-xl mt-1.5" data-testid="album-category-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                                    <SelectItem key={k} value={k}>{v}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className="text-xs text-slate-500 mt-1.5">We'll auto-detect this from the name if you skip it.</p>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} className="rounded-full" type="button">Cancel</Button>
                    <Button onClick={create} disabled={busy || !name.trim()} className="rounded-full bg-primary hover:bg-primary/90 text-white" data-testid="create-album-submit">
                        {busy ? "Creating…" : "Create album"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function EditAlbumDialog({ album, onClose, onSaved }) {
    const [name, setName] = useState("");
    const [category, setCategory] = useState("other");
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        if (album) {
            setName(album.name || "");
            setCategory(album.category || "other");
        }
    }, [album]);
    if (!album) return null;

    const trimmed = name.trim();
    const nameChanged = trimmed && trimmed !== album.name;

    async function save() {
        if (!trimmed) {
            toast.error("Album name cannot be empty");
            return;
        }
        setBusy(true);
        try {
            const payload = { category };
            if (nameChanged) payload.name = trimmed;
            await api.put(`/photos/albums/${album.id}`, payload);
            toast.success(nameChanged ? `Renamed to "${trimmed}"` : "Album updated");
            onSaved?.();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        setBusy(false);
    }

    return (
        <Dialog open={!!album} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md">
                <DialogHeader><DialogTitle className="font-heading text-2xl" style={{ color: NAVY }}>Edit "{album.name}"</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-2">
                    <div>
                        <Label htmlFor="edit-album-name-input" className="text-slate-900">Title</Label>
                        <Input
                            id="edit-album-name-input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Album title"
                            className="rounded-xl mt-1.5"
                            data-testid="edit-album-name-input"
                        />
                        <p className="text-xs text-slate-500 mt-1.5">
                            Existing photos move with the new title automatically.
                            {album.is_default && (
                                <> Renaming this default album will convert it into a custom album (the original name won't be re-created on the next boot).</>
                            )}
                        </p>
                    </div>
                    <div>
                        <Label className="text-slate-900">Category</Label>
                        <Select value={category} onValueChange={setCategory}>
                            <SelectTrigger className="rounded-xl mt-1.5" data-testid="edit-album-category-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                                    <SelectItem key={k} value={k}>{v}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <p className="text-xs text-slate-500">To change the cover photo, open the album and click the ⭐ on any photo.</p>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} className="rounded-full" type="button">Cancel</Button>
                    <Button onClick={save} disabled={busy || !trimmed} className="rounded-full bg-primary hover:bg-primary/90 text-white" data-testid="edit-album-save-btn">
                        {busy ? "Saving…" : "Save"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
